import { Injectable } from "@angular/core";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { AtdlTooltipUiRep } from "shared/cubiq-services";
// multi-asset
import * as collate from "pouchdb-collate";
////////////////////////////////
import { ATDLUtilityService, ICD_CLOCK_TIME_PATTERN } from "./atdl-utility.service";
import { ATDLParserService } from "./atdl-parser.service";
///
import { ATDLDataModel } from "../../data-atdl/model/atdl-data.model";
// paramter model
import { ATDLParameterDataModel } from "../../data-atdl/model/atdl-parameter-data.model";
import { ATDLEnumModel } from "../../data-atdl/model/atdl-enum.model";
// interface models
import { ATDLInterfaceLayoutModel } from "../../data-atdl/model/atdl-interface-layout.model";
import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
import { ATDLLayoutControlModel } from "../../data-atdl/model/atdl-layout-control.model";

// validation models
import { ATDLInterfaceDependencyModel } from "../../data-atdl/model/atdl-interface-dependency.model";
import { ATDLInterfaceAuxiliaryModel } from "../../data-atdl/model/atdl-interface-auxiliary.model";
// ENUMS
import { ATDLNOrientation } from "../../data-atdl/enums/atdl-orientation.enum";
import { ATDLDatasetService } from "./atdl-dataset.service";
import { PARAM_TO_FIX_MAPPING } from "../../data-atdl/mappings/atdl-param-type-to-fix-type.const";
import { ATDLNControlType } from "../../data-atdl/enums/atdl-control-type.enum";
import { ATDLNParameterType } from "../../data-atdl/enums/atdl-parameter-type.enum";
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";
import { ICD_ID_LIMIT_PRICE } from "app/order-entry/interface/oe-panel-algo-params.const";

@Injectable()
export class ATDLProcessorService {
  /**********************************************************************/
  /* CONSTRUCTOR  */

  constructor(
    private atdlUtility: ATDLUtilityService,
    private atdlParser: ATDLParserService,
    private atdlDataset: ATDLDatasetService,
    private tooltipUiRep: AtdlTooltipUiRep,
  ) {
    this.initialize();
  }
  /**********************************************************************/
  /* INITIALIZATION */
  public reinitialize() {
    this.atdlDataset.reset();
    this.tooltipUiRep.reset();
    this.initialize();
  }

  private initialize() {
    this.atdlParser
      .initializeAlgoDefinitions()
      .then(
        () => {
          if (this.atdlDataset.isPrimed()) {
            for (let model of this.atdlDataset.getAllAsArray()) {
              this.processATDLModel(model);
            }
            this.tooltipUiRep.resolve();
          } else {
            logger.out.error(`ERROR: atdlProcessor.initialize(): ATDL Dataset not primed!`);

            // allow prime to continue
            this.tooltipUiRep.resolve();
          }
        },
        () => {
          logger.out.error(`ERROR: atdlProcessor.initialize(): ATDL Dataset not primed!`);

          // allow prime to continue
          this.tooltipUiRep.resolve();
        },
      )
      .finally(() => {
        if (this.atdlDataset.isEmpty()) return logger.out.info(`NO AVAILABLE ALGO STRATEGIES`);

        logger.out.info(
          `AVAILABLE ALGO STRATEGIES: (${
            this.atdlDataset.getAllAsArray().length
          }) -> [${this.atdlDataset.getAllAsArray().map((item) => item.uiRep)}]`,
        );
        logger.out.info(
          `INVALID ALGO STRATEGIES: (${
            this.atdlDataset.getInvalidAlgoIds().length
          }) -> [${this.atdlDataset.getInvalidAlgoIds()}]`,
        );
      });
  }

  /**********************************************************************/
  /* POST-PARSING PROCESS */

  public processATDLModel(model: ATDLDataModel): void {
    try {
      // find additional controls
      this.detectAndAttachAuxiliaryControls(model);
      // add meta-data to models
      this.attachMetaDataToControls(model);
      this.bindControlsAndParameters(model);
      // should be last
      this.createLayoutStructure(model);
      //
      this.evaluateLimitPriceMode(model);
    } catch (ex) {
      logger.out.error(
        `ERROR: atdlProcessor.processATDLModel() - deleting defn for '${model?.id}': Exception:`,
        ex,
      );
      this.atdlDataset.delete(model.id);
    }
  }

  /**********************************************************************/
  /* LAYOUT STRUCTURE METHODS */

  private createLayoutStructure(model: ATDLDataModel): void {
    if (model instanceof ATDLDataModel && model.layouts instanceof Array) {
      if (model.layouts.length > 1) {
        // dev-test error should be a warning
        logger.out.warn(`WARNING: atdlParser.createLayoutStructure(): Root has multiple layouts!`);
      }

      model.layoutStructure = [];
      this.processLayout(model.controls, model.layouts, model.layoutStructure);
    }
  }

  /**
   *
   * @param controls
   * @param layouts
   * @param layoutStructure
   *
   * @returns true if the layout contains limit price but was not processed
   */
  private processLayout(
    controls: Array<ATDLInterfaceControlModel>,
    layouts: Array<ATDLInterfaceLayoutModel>,
    layoutStructure: Array<any>,
  ): void {
    if (Array.isArray(layouts) && layouts.length > 0 && Array.isArray(layoutStructure)) {
      for (let layout of layouts) {
        if (layout instanceof ATDLInterfaceLayoutModel) {
          // process controls
          if (layout.controls instanceof Array && layout.controls.length > 0) {
            if (layout.orientation === ATDLNOrientation.HORIZONTAL) {
              for (let i = 0; i < layout.controls.length; i++) {
                let controlModel = controls.find((item) => item.icdId === layout.controls[i]);
                if (controlModel instanceof ATDLInterfaceControlModel) {
                  if (i % 2 === 0) {
                    let row = new Array<any>();
                    let control = new ATDLLayoutControlModel(
                      controlModel.icdId,
                      controlModel.refName,
                      controlModel.icdType,
                      controlModel.paramType,
                    );
                    row.push(control);
                    layoutStructure.push(row);
                  } else {
                    let control = new ATDLLayoutControlModel(
                      controlModel.icdId,
                      controlModel.refName,
                      controlModel.icdType,
                      controlModel.paramType,
                    );
                    if (layoutStructure[layoutStructure.length - 1] instanceof Array) {
                      layoutStructure[layoutStructure.length - 1].push(control);
                    } else {
                      // only when the limitPrice was in the same row and excluded
                      let row = new Array<any>(control);
                      layoutStructure.push(row);
                    }
                  }
                } else {
                  throw new Error(
                    `Control model '${layout.controls[i]}' NOT FOUND while processing layout`,
                  );
                }
              }
            } else {
              // VERTICAL
              for (let i = 0; i < layout.controls.length; i++) {
                let controlModel = controls.find((item) => item.icdId === layout.controls[i]);
                if (controlModel instanceof ATDLInterfaceControlModel) {
                  let row = new Array<any>();
                  let control = new ATDLLayoutControlModel(
                    controlModel.icdId,
                    controlModel.refName,
                    controlModel.icdType,
                    controlModel.paramType,
                  );
                  row.push(control);
                  layoutStructure.push(row);
                } else {
                  throw new Error(
                    `Control model '${layout.controls[i]}' NOT FOUND while processing layout`,
                  );
                }
              }
            }
          }

          // process layouts
          if (Array.isArray(layout.layouts) && layout.layouts.length > 0) {
            this.processLayout(controls, layout.layouts, layoutStructure);
          }
        }
      }
    }
  }

  /**********************************************************************/
  /* AUX-CONTROL METHODS */

  private detectAndAttachAuxiliaryControls(model: ATDLDataModel): void {
    if (model instanceof ATDLDataModel && model.controls instanceof Array) {
      for (let control of model.controls) {
        if (control.icdType === ATDLNControlType.CHECKBOX && control.refName === null) {
          let ownerControl = model.controls.filter((item: ATDLInterfaceControlModel) => {
            return item.dependencies instanceof Array && item.dependencies.length > 0
              ? item.dependencies.find((item) => item.singleFieldDependency === control.icdId) !=
                  null
              : false;
          });
          let noAuxControls = [
            ATDLNControlType.CHECKBOX,
            ATDLNControlType.LABEL,
            ATDLNControlType.RADIO_LIST,
          ];
          // if there is only one dependant on the checkbox, attach it as Aux control
          if (
            ownerControl instanceof Array &&
            ownerControl.length === 1 &&
            !noAuxControls.includes(ownerControl[0].icdType)
          ) {
            let auxiliaryModel: ATDLInterfaceAuxiliaryModel = new ATDLInterfaceAuxiliaryModel();
            auxiliaryModel.icdId = control.icdId;
            auxiliaryModel.refIcdId = ownerControl[0].icdId;
            auxiliaryModel.label = control.label;
            auxiliaryModel.icdType = control.icdType;
            auxiliaryModel.initValue =
              control.initValue === "true" || control.initValue === "false"
                ? control.initValue === "true"
                : null;
            auxiliaryModel.dependencies =
              control.dependencies instanceof Array && control.dependencies.length > 0
                ? Object.assign(new Array<ATDLInterfaceDependencyModel>(), control.dependencies)
                : null;

            if (!(ownerControl[0].auxiliary instanceof Array)) {
              ownerControl[0].auxiliary = new Array<ATDLInterfaceAuxiliaryModel>();
            }
            // attach the control as aux control
            ownerControl[0].auxiliary.push(auxiliaryModel);
            // remove control as independent control
            model.controls = model.controls.filter((item) => item.icdId != control.icdId);
            // delete from layout
            this.deleteControlFromLayout(model.layouts, control.icdId);
          }
        }
      }
    } else {
      throw new Error(`Model is invalid or has no controls`);
    }
  }

  private deleteControlFromLayout(layouts: Array<ATDLInterfaceLayoutModel>, icdId: string): void {
    if (layouts instanceof Array && layouts.length > 0) {
      for (let layout of layouts) {
        if (layout.controls instanceof Array && layout.controls.length > 0) {
          layout.controls = layout.controls.filter((item) => item != icdId);
        }
        if (layout.layouts instanceof Array && layout.layouts.length > 0) {
          this.deleteControlFromLayout(layout.layouts, icdId);
        }
      }
    }
  }

  /**********************************************************************/
  /* META-DATA METHODS */

  private attachMetaDataToControls(model: ATDLDataModel): void {
    if (model instanceof ATDLDataModel && model.controls instanceof Array) {
      let algoId = model.name;
      for (let control of model.controls) {
        control.algoId = algoId;
        // attach label to main control from the auxiliary if not specified
        if (
          (control.label == null || control.label === "") &&
          control.auxiliary instanceof Array &&
          control.auxiliary.length > 0
        ) {
          control.label = control.auxiliary[0].label;
        }
      }
    } else {
      throw new Error(`Model is invalid or has no controls`);
    }
  }

  private bindControlsAndParameters(model: ATDLDataModel): void {
    if (
      model instanceof ATDLDataModel &&
      model.controls instanceof Array &&
      model.parameters instanceof Array &&
      model.controls.length > 0 &&
      model.parameters.length > 0
    ) {
      let algoId = model.name;
      for (let parameter of model.parameters) {
        if (parameter instanceof ATDLParameterDataModel) {
          // control independent
          parameter.algoId = algoId;
          parameter.fixType = PARAM_TO_FIX_MAPPING.get(parameter.paramType);
          this.evaluateAndAdjustMinMaxValues(parameter);
          // control dependent
          let control = model.controls.find((item) => item.refName === parameter.refName);
          if (control instanceof ATDLInterfaceControlModel) {
            parameter.icdId = control.icdId;
            parameter.icdType = control.icdType;
            if (
              control.dataset instanceof Array &&
              parameter.dataset instanceof Array &&
              control.dataset.length > 0
            ) {
              for (let controlEnumItem of control.dataset) {
                let parameterEnumItem = parameter.dataset.find(
                  (item) => item.enumId === controlEnumItem.enumId,
                );
                if (parameterEnumItem instanceof ATDLEnumModel) {
                  parameterEnumItem.uiRep = controlEnumItem.uiRep;
                  controlEnumItem.wireValue = parameterEnumItem.wireValue;
                  // logger.out.info(`${this.constructor.name}.bindControlsAndParameters() tooltip lookup created: ` +
                  // 	`${algoId.toString()}:${parameter.refName.toString()}:${parameterEnumItem.wireValue.toString()}` +
                  // 	` ${parameterEnumItem.uiRep}`
                  // );
                  this.tooltipUiRep.setUiRep(
                    collate.toIndexableString([
                      model.name,
                      parameter.refName,
                      parameterEnumItem.wireValue,
                    ]),
                    parameterEnumItem.uiRep,
                  );

                  // divergence is strictly speaking possible (esp. case) and occurs more drastically
                  // on some configs
                  // while server will often use model.id esp in AlgoHedgeConfigInfoImpl
                  // TODO possibly to use only model.name internally
                  // (HOM Staging Editor use of param editor is notable here)...
                  if (model.id !== model.name)
                    this.tooltipUiRep.setUiRep(
                      collate.toIndexableString([
                        model.id,
                        parameter.refName,
                        parameterEnumItem.wireValue,
                      ]),
                      parameterEnumItem.uiRep,
                    );
                } else {
                  throw new Error(
                    `${
                      ATDLNControlType[control.icdType]
                    } incompatible control/parameter data-item for ${controlEnumItem.enumId}`,
                  );
                }
              }
              // if more than 2 options for Radio button, cut the rest (as per KD)
              if (control.icdType === ATDLNControlType.RADIO_LIST && control.dataset.length > 2) {
                control.dataset = control.dataset.slice(0, 2);
                parameter.dataset = parameter.dataset.slice(0, 2);
                logger.out.warn(
                  `WARNING: atdlParser.bindControlsAndParameters(): ${
                    ATDLNControlType[ATDLNControlType.RADIO_LIST]
                  } dataset reduced to 2 options for '${parameter.refName}' parameter and control.`,
                );
              }
            }

            parameter.hasAuxiliaryCheckbox =
              control.auxiliary instanceof Array && control.auxiliary.length >= 1;
            parameter.uncheckedEnumRef = control.uncheckedEnumRef;
            parameter.checkedEnumRef = control.checkedEnumRef;
            // map parameter to control validation
            control.paramType = parameter.paramType;
            control.validity.required =
              typeof parameter.use === "string" ? parameter.use === "required" : false;

            if (
              typeof parameter.precision === "number" &&
              !isNaN(parameter.precision) &&
              typeof parameter.minValue === "number" &&
              !isNaN(parameter.minValue) &&
              parameter.minValue != 0 &&
              parameter.minValue < 10 ** (parameter.precision * -1)
            ) {
              control.validity.minValue = 10 ** (parameter.precision * -1);
              logger.out.info(
                `${this.constructor.name}.bindControlsAndParameters() %o adjusting` +
                  ` minValue from ${parameter.minValue} to ${control.validity.minValue}` +
                  ` due to configured precision ${parameter.precision}`,
                parameter,
              );
            } else {
              control.validity.minValue = parameter.minValue;
            }

            control.validity.maxValue = parameter.maxValue;
            control.validity.minLength = parameter.minLength;
            control.validity.maxLength = parameter.maxLength;
            control.precision = parameter.precision;
            // BFX property
            control.disableOnPartFill = parameter.disableOnPartFill =
              control.disableOnPartFill || parameter.disableOnPartFill;
          } else if (typeof parameter.constValue === "string") {
            let associatedControl = model.controls.find(
              (item) => item.refName === parameter.constValue,
            );
            if (associatedControl instanceof ATDLInterfaceControlModel) {
              parameter.associatedIcdId = associatedControl.icdId;
              parameter.icdType = associatedControl.icdType;
            } else {
              // when const value is not depended on control value, but its hard-coded
              parameter.value = parameter.constValue;
            }
          } else {
            logger.out.error(
              `ERROR: atdlParser.bindControlsAndParameters(): Control or AssociatedControl not found for parameter '${parameter.refName}' for Algo '${model.id}'`,
            );
          }
        } else {
          throw new Error(
            `ERROR: atdlParser.bindControlsAndParameters(): Invalid parameter model for Algo '${model.id}'`,
          );
        }
      }
    } else {
      logger.out.error(
        `ERROR: atdlParser.bindControlsAndParameters(): Invalid controls or parameters for Algo '${model.id}'`,
      );
    }
  }

  private evaluateAndAdjustMinMaxValues(parameter: ATDLParameterDataModel): void {
    // evaluate local validation min max values
    if (
      parameter.paramType === ATDLNParameterType.STRING ||
      parameter.paramType === ATDLNParameterType.MULTICHAR ||
      parameter.paramType === ATDLNParameterType.MULTISTRING
    ) {
      parameter.minValue = null;
      parameter.maxValue = null;
    }

    if (
      parameter.paramType === ATDLNParameterType.UTCTIMEONLY ||
      parameter.paramType === ATDLNParameterType.UTCTIMESTAMP
    ) {
      // min format
      if (
        typeof parameter.minValue === "string" &&
        !ICD_CLOCK_TIME_PATTERN.test(parameter.minValue)
      ) {
        parameter.minValue = null;
      }
      // max format
      if (
        typeof parameter.maxValue === "string" &&
        !ICD_CLOCK_TIME_PATTERN.test(parameter.maxValue)
      ) {
        parameter.maxValue = null;
      }
      // min greater than max
      if (
        typeof parameter.minValue === "string" &&
        typeof parameter.maxValue === "string" &&
        this.atdlUtility.compareTimeGreaterOrEqualThan(parameter.minValue, parameter.maxValue)
      ) {
        parameter.minValue = null;
      }
    } else {
      if (
        typeof parameter.minValue === "number" &&
        typeof parameter.maxValue === "number" &&
        parameter.minValue > parameter.maxValue
      ) {
        parameter.minValue = null;
      }
      // check if min is >=0 for following types (essentially Integer and Percent spinners)
      // TODO Discuss with product
      // if (
      //   parameter.paramType === ATDLNParameterType.AMT ||
      //   parameter.paramType === ATDLNParameterType.PRICE ||
      //   parameter.paramType === ATDLNParameterType.QTY ||
      //   parameter.paramType === ATDLNParameterType.PERCENT
      // ) {
      //   if (typeof parameter.minValue === "number" && parameter.minValue < 0) {
      //     parameter.minValue = null;
      //   }
      //   if (typeof parameter.maxValue === "number" && parameter.maxValue < 0) {
      //     parameter.maxValue = null;
      //   }
      // }
    }
    // evaluate local validation min max length
    if (
      parameter.paramType !== ATDLNParameterType.STRING &&
      parameter.paramType !== ATDLNParameterType.MULTICHAR &&
      parameter.paramType !== ATDLNParameterType.MULTISTRING
    ) {
      parameter.minLength = null;
      parameter.maxLength = null;
    }
    if (
      typeof parameter.minLength === "number" &&
      typeof parameter.maxLength === "number" &&
      parameter.minLength > parameter.maxLength
    ) {
      parameter.minLength = null;
    }
    if (typeof parameter.minLength === "number" && parameter.minLength < 0) {
      parameter.minLength = null;
    }
    if (typeof parameter.maxLength === "number" && parameter.maxLength < 0) {
      parameter.maxLength = null;
    }
  }

  private evaluateLimitPriceMode(model: ATDLDataModel): void {
    if (
      model instanceof ATDLDataModel &&
      model.controls instanceof Array &&
      model.controls.length > 0
    ) {
      let limitPriceControl = model.controls.find(
        (item) => item.icdId.toLowerCase() === ICD_ID_LIMIT_PRICE,
      );
      if (limitPriceControl instanceof ATDLInterfaceControlModel) {
        if (
          limitPriceControl.auxiliary instanceof Array &&
          limitPriceControl.auxiliary.length > 0
        ) {
          model.limitPriceMode = ICDNAuxMode.OPTIONAL;
        } else {
          model.limitPriceMode = ICDNAuxMode.REQUIRED;
        }
      } else {
        model.limitPriceMode = ICDNAuxMode.DISABLED;
      }
    }
  }
}
